import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Pagination,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useOrders } from '../hooks/useOrders';
import SelectedOrdersActions from '../components/orders/SelectedOrdersActions';
import OrdersTable from '../components/orders/OrdersTable';
import OrderDetailsModal from '../components/orders/OrderDetailsModal';
import CreateManualOrderModal from '../components/orders/CreateManualOrderModal';
import ChangeStatusModal from '../components/orders/ChangeStatusModal';

/**
 * Page principale des commandes - Version refactorisée
 */
const Orders = () => {
  const { isAuthenticated, user } = useAuth();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  // Status Modal State
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusTargetIds, setStatusTargetIds] = useState(null); // Array or Set of IDs
  // Déterminer le statut admin (prise en charge des booléens et des indicateurs numériques)
  const adminFlag = user?.isAdmin ?? user?.is_admin;
  const isAdmin = adminFlag === true || adminFlag === 1 || adminFlag === '1';

  // Debug: afficher les informations utilisateur (à retirer en production)
  // useEffect(() => {
  //   if (user) {
  //     console.log('🔍 User object:', user);
  //     console.log('🔍 isAdmin check:', {
  //       isAdminProperty: user?.isAdmin,
  //       is_adminProperty: user?.is_admin,
  //       finalIsAdmin: isAdmin,
  //     });
  //   }
  // }, [user, isAdmin]);

  const {
    // État
    orders,
    loading,
    selectedOrder,
    detailDialogOpen,
    page,
    totalPages,
    error,
    selectedOrders,
    search,
    debouncedSearch,

    // Actions
    setPage,
    setDetailDialogOpen,
    fetchOrders,
    setSearch,
    handleViewDetails,
    handleOrderSelect,
    handleSelectAll,
    handleToggleOrderSelection,
    handleBulkCancel,
    handlePrint,
    handleDownloadInvoice,
    handleCancelOrder,
    handleGenerateLabel,
    handleValidateOrder,
    handleMarkAsShipped,
    handleBulkStatusChange,
  } = useOrders();

  useEffect(() => {
    setSearchInput(search || '');
  }, [search]);

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Vous devez être connecté pour voir vos commandes.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const handleOpenBulkStatusModal = () => {
    if (selectedOrders.size > 0) {
      setStatusTargetIds(selectedOrders);
      setStatusModalOpen(true);
    }
  };

  const handleOpenSingleStatusModal = (orderId) => {
    setStatusTargetIds([orderId]);
    setStatusModalOpen(true);
  };

  const handleStatusSubmit = (newStatus) => {
    if (statusTargetIds) {
      handleBulkStatusChange(statusTargetIds, newStatus);
      setStatusModalOpen(false);
      setStatusTargetIds(null);
    }
  };

  const handleCreateOrderSuccess = async (newOrder) => {
    setCreateModalOpen(false);
    // Rafraîchir la liste des commandes
    await fetchOrders();
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      setPage(1);
      setSearch(searchInput.trim());
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h4">
          {isAdmin ? 'Toutes les Commandes' : 'Mes Commandes'}
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            size="small"
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            placeholder="Recherche n° ou client"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260 }}
          />
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateModalOpen(true)}
            >
              Créer une commande
            </Button>
          )}
        </Box>
      </Box>

      {/* Barre d'actions pour les commandes sélectionnées */}
      <SelectedOrdersActions
        selectedOrders={selectedOrders}
        orders={orders}
        onBulkCancel={handleBulkCancel}
        onPrint={() => handlePrint()}
        onBulkStatusChange={handleOpenBulkStatusModal}
      />

      {orders.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Aucune commande trouvée
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Vous n'avez pas encore passé de commande.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Tableau des commandes */}
          <OrdersTable
            orders={orders}
            selectedOrders={selectedOrders}
            onOrderSelect={handleOrderSelect}
            onSelectAll={handleSelectAll}
            onToggleOrderSelection={handleToggleOrderSelection}
            onViewDetails={handleViewDetails}
            onPrint={handlePrint}
            onDownloadInvoice={handleDownloadInvoice}
            onGenerateLabel={handleGenerateLabel}
            onStatusClick={handleOpenSingleStatusModal}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: 'text.primary',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Modal des détails de commande */}
      <OrderDetailsModal
        open={detailDialogOpen}
        order={selectedOrder}
        onClose={() => setDetailDialogOpen(false)}
        onCancelOrder={handleCancelOrder}
        onValidateOrder={handleValidateOrder}
        onDownloadInvoice={handleDownloadInvoice}
        onMarkAsShipped={handleMarkAsShipped}
      />

      {/* Modal de changement de statut */}
      <ChangeStatusModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onSubmit={handleStatusSubmit}
        count={statusTargetIds ? (Array.isArray(statusTargetIds) ? statusTargetIds.length : statusTargetIds.size) : 0}
      />

      {/* Modal de création de commande manuelle */}
      {isAdmin && (
        <CreateManualOrderModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handleCreateOrderSuccess}
        />
      )}
    </Box>
  );
};

export default Orders;
